// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

import React from "react";
import { readableOn, inkOn } from "../lib/contrast";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

/**
 * A label on the plan.
 *
 * Two shapes. A chip -- solid fill in the element's own colour, type picked
 * for contrast against it, fully rounded -- and a halo, which is bare type
 * with the surface painted as a stroke behind the glyphs. Chips carry the
 * measurements; halos carry the names, because a dozen filled pills over a
 * drawing is the thing that made the plan unreadable.
 *
 * The halo is the part that is easy to get wrong, and was. Its whole trick is
 * that the stroke *becomes* the local background, so it has to be the colour
 * of the paper rather than the colour of the app. Painting the dark panel
 * colour there put a near-black outline around dark type on a white sheet and
 * turned every name into a smudge.
 *
 * So the caller passes the surface the label actually sits on -- the drawing,
 * not the panel -- and the type is pushed until it reads against it while
 * keeping its hue, so the colour still says which element the name belongs to.
 *
 * Width is estimated from the character count rather than measured. Inter at
 * 11px averages ~0.56em; a <foreignObject> would auto-size but tends to be
 * dropped by PDF and PNG exporters, and a plan that prints wrong is worse
 * than a chip that is a few pixels wide of perfect.
 */
export default function MapLabel({
  x, y, text, color, variant = "solid", size = 11, dy = 0, surface,
}: {
  x: number; y: number; text: string; color: string;
  variant?: "solid" | "quiet" | "halo"; size?: number; dy?: number; surface: string;
}) {
  if (!text) return null;

  // No chrome: the surface colour is painted as a stroke behind the glyphs,
  // which is how a map keeps text readable over whatever is underneath it.
  if (variant === "halo") {
    return (
      <text x={x} y={y + dy} textAnchor="middle" dominantBaseline="central"
        fontSize={size} fontWeight={600} letterSpacing=".01em"
        fill={inkOn(color, surface)} stroke={surface}
        strokeWidth={Math.max(2.5, size * 0.3)} strokeLinejoin="round" paintOrder="stroke"
        style={{ fontFamily: FONT, pointerEvents: "none", userSelect: "none" }}>
        {text}
      </text>
    );
  }
  const padX = 7, padY = 3.5;
  const w = Math.max(text.length * size * 0.56 + padX * 2, size + padX * 2);
  const h = size + padY * 2;
  const cy = y + dy;
  const solid = variant === "solid";
  return (
    <g style={{ pointerEvents: "none", userSelect: "none" }}>
      <rect x={x - w / 2} y={cy - h / 2} width={w} height={h} rx={h / 2}
        fill={solid ? color : surface}
        stroke={solid ? "none" : color}
        strokeWidth={solid ? 0 : 1}
        opacity={solid ? 1 : 0.92} />
      <text x={x} y={cy} textAnchor="middle" dominantBaseline="central"
        fontSize={size} fontWeight={solid ? 700 : 600}
        letterSpacing=".01em"
        fill={solid ? readableOn(color) : readableOn(surface)}
        style={{ fontFamily: FONT }}>
        {text}
      </text>
    </g>
  );
}
